import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { Spinner } from '@/src/components/ui/spinner';
import { cn } from '@/src/lib/utils';

const buttonVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center gap-2 rounded-lg text-[13px] font-semibold whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-[var(--spark-shadow-sm)] hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline: 'border bg-card text-foreground shadow-[var(--spark-shadow-sm)] hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 px-3 py-1.5 has-[>svg]:px-2.5',
        sm: 'h-7 gap-1.5 rounded-md px-2.5 text-[12px] has-[>svg]:px-2',
        lg: 'h-9 rounded-lg px-4 has-[>svg]:px-3',
        icon: 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  'aria-disabled': ariaDisabled,
  disabled,
  children,
  loading = false,
  loadingLabel,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    loadingLabel?: string;
  }) {
  const Comp = asChild ? Slot.Root : 'button';
  const isLoading = Boolean(loading);
  const isIcon = size === 'icon';
  const busyLabel = loadingLabel ?? 'Loading';
  const disabledState = disabled || isLoading;

  if (asChild) {
    return (
      <Comp
        aria-busy={isLoading || undefined}
        aria-disabled={disabledState || ariaDisabled || undefined}
        data-loading={isLoading || undefined}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {isLoading ? <Spinner /> : null}
        <Slot.Slottable>{children}</Slot.Slottable>
      </Comp>
    );
  }

  return (
    <Comp
      aria-busy={isLoading || undefined}
      aria-disabled={ariaDisabled}
      data-loading={isLoading || undefined}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabledState}
      {...props}
    >
      {isLoading ? (
        <>
          <span aria-hidden="true" className="inline-flex min-w-0 items-center justify-center gap-2 opacity-0">
            {children}
          </span>
          <span className="absolute inset-0 inline-flex items-center justify-center gap-2 px-2">
            <Spinner />
            {isIcon ? <span className="sr-only">{busyLabel}</span> : <span>{busyLabel}</span>}
          </span>
        </>
      ) : children}
    </Comp>
  );
}

export { Button, buttonVariants };
